import React, { FC, useCallback, useMemo } from 'react';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import {
  Operation,
  OPERATIONS,
  Resource,
  RESOURCES,
} from '../../common/constants';
import TableBody from '@material-ui/core/TableBody';
import CheckBox from '@material-ui/core/Checkbox';
import { PermissionInput } from '../../types/graphql-global-types';
import TableContainer, {
  TableContainerProps,
} from '@material-ui/core/TableContainer';
import { Box, IconButton } from '@material-ui/core';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import MobileOffIcon from '@material-ui/icons/MobileOff';
import BusinessIcon from '@material-ui/icons/Business';
import DomainDisabledIcon from '@material-ui/icons/DomainDisabled';

type Permissions = (PermissionInput | null)[] | null | undefined;

interface IPermissionsManager extends Pick<TableContainerProps, 'key'> {
  permissions: Permissions;
  onChange: (permission: PermissionInput) => void;
  loading?: boolean;
}

const isDisabled = (resource: Resource, operation: Operation): boolean => {
  switch (resource) {
    case Resource.RATES:
    case Resource.ROLES:
      return ![Operation.READ, Operation.UPDATE].includes(operation);
    case Resource.FEEDBACKS:
      return [Operation.IMPORT, Operation.EXPORT].includes(operation);
    case Resource.COMPAIGNS:
      return ![Operation.CREATE, Operation.READ].includes(operation);
    default:
      return false;
  }
};

const PermissionsManager: FC<IPermissionsManager> = (props) => {
  const { permissions, loading, onChange, ...others } = props;
  const canAccess = useCallback(
    (resource: Resource) =>
      permissions?.findIndex((p) => p?.resource === resource && !!p?.read) !==
      -1,
    [permissions],
  );

  const canAccessMobile = useMemo<boolean>(
    () => canAccess(Resource.MOBILE),
    [canAccess],
  );
  const canAccessBackoffice = useMemo<boolean>(
    () => canAccess(Resource.BACKOFFICE),
    [canAccess],
  );

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-evenly"
        alignItems="center"
        p={1}>
        <IconButton
          onClick={() =>
            onChange({
              resource: Resource.MOBILE,
              read: !canAccessMobile,
            })
          }>
          {canAccessMobile ? (
            <MobileFriendlyIcon fontSize="large" color="primary" />
          ) : (
            <MobileOffIcon fontSize="large" color="disabled" />
          )}
        </IconButton>
        <IconButton
          onClick={() =>
            onChange({
              resource: Resource.BACKOFFICE,
              read: !canAccessBackoffice,
            })
          }>
          {canAccessBackoffice ? (
            <BusinessIcon fontSize="large" color="primary" />
          ) : (
            <DomainDisabledIcon fontSize="large" color="disabled" />
          )}
        </IconButton>
      </Box>
      <TableContainer {...others}>
        <Table style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 50 }} />
              {Object.keys(Operation)?.map((operation, j) => (
                <TableCell key={`roles-operations-cell-${j}`}>
                  {OPERATIONS[operation] ?? operation}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions?.map(
              (permission, l) =>
                permission?.resource &&
                ![Resource.BACKOFFICE, Resource.MOBILE].includes(
                  permission?.resource as Resource,
                ) && (
                  <TableRow key={`roles-permissions-row-${l}`}>
                    <TableCell variant="head">
                      {RESOURCES[permission?.resource] ?? permission?.resource}
                    </TableCell>
                    {Object.keys(Operation).map((operation, k) => (
                      <TableCell
                        key={`roles-operation-cell-${k}`}
                        padding="checkbox"
                        align="center">
                        <CheckBox
                          color="primary"
                          disabled={
                            isDisabled(
                              permission?.resource as Resource,
                              operation as Operation,
                            ) || !!loading
                          }
                          name={operation.toLowerCase()}
                          onClick={(event: any) =>
                            onChange({
                              resource: permission?.resource,
                              [event.target.name]: event.target.checked, // operations like create, read, ...
                            })
                          }
                          checked={
                            !!permission[
                              operation.toLowerCase() as keyof PermissionInput
                            ]
                          }
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ),
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PermissionsManager;
